import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TrendingUp } from "lucide-react";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import relatoriosService from "@/features/relatorios/services/relatoriosService";
import type { WidgetProps } from "@/features/dashboard/types";

// Data LOCAL (não usar toISOString: em UTC "hoje" vira amanhã depois das 21h BRT)
const dataLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

type Periodo = "hoje" | "7d" | "mes";

const PERIODOS: Record<Periodo, { rotulo: string; descricao: string; inicio: () => string }> = {
  hoje: { rotulo: "Hoje",    descricao: "Produtos e serviços mais vendidos hoje",           inicio: () => dataLocal(new Date()) },
  "7d": { rotulo: "7 dias",  descricao: "Mais vendidos nos últimos 7 dias",                 inicio: () => { const d = new Date(); d.setDate(d.getDate() - 6); return dataLocal(d); } },
  mes:  { rotulo: "Mês",     descricao: "Mais vendidos no mês corrente",                    inicio: () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; } },
};

const fmtMoeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtQtd = (v: number) =>
  v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

export function TopProducts({ config, onConfigChange }: WidgetProps = {}) {
  const { temPermissao } = usePermissoes();
  const podeVer = temPermissao(FORMULARIOS.RELATORIOS_VENDAS, "ler");

  // Config por instância (Fase 4): período. No modo edição a escolha é
  // persistida junto com o layout; fora dele vale só para a sessão.
  const [periodoLocal, setPeriodoLocal] = useState<Periodo | null>(null);
  const configurado = config?.periodo as Periodo | undefined;
  const periodo: Periodo = periodoLocal ?? (configurado && configurado in PERIODOS ? configurado : "hoje");

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-top-itens", periodo, PERIODOS[periodo].inicio()],
    queryFn: () =>
      relatoriosService.vendas.produtosSintetico({
        dtInicio: PERIODOS[periodo].inicio(),
        dtFim: dataLocal(new Date()),
        pagina: 1,
        tamanhoPagina: 5,
      }),
    enabled: podeVer,
    staleTime: 60_000,
  });

  if (!podeVer) return null;

  const itens = data?.itens ?? [];

  return (
    <Card className="shadow-card border-border/50 h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
            <TrendingUp className="h-4 w-4 text-primary" />
          </div>
          <span className="flex-1">Mais Vendidos</span>
          <div className="widget-no-drag">
            <Select
              value={periodo}
              onValueChange={(v) => {
                setPeriodoLocal(v as Periodo);
                onConfigChange?.({ ...config, periodo: v });
              }}
            >
              <SelectTrigger className="h-7 w-24 text-xs font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERIODOS).map(([k, p]) => (
                  <SelectItem key={k} value={k}>{p.rotulo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
        <CardDescription className="text-xs">{PERIODOS[periodo].descricao}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-lg" />
            ))}
          </div>
        ) : itens.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhuma venda registrada no período.
          </p>
        ) : (
          <div className="space-y-3">
            {itens.map((item, index) => (
              <div key={item.produtoId} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/8 text-xs font-bold font-heading text-primary">
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.descricao}</p>
                    <p className="text-xs text-muted-foreground">{fmtQtd(item.qtdTotal)} unidades</p>
                  </div>
                </div>
                <p className="text-sm font-semibold font-heading text-foreground">{fmtMoeda(item.valorTotal)}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
